<!--
  OptionPicker:区号 / State 半屏底部抽屉选择器
  - PanelModal 容器 + 三段结构:标题栏(header) + 选项列表(default slot) + Cancel 按钮(footer)
  - D-18 + UI-SPEC revision 2 硬约束:不渲染字母索引边栏(已支持查询输入框)
  - 选中态前缀 ✓ 颜色 #F36F12(accent 微观使用,和 checkbox 同逻辑)
  - 本组件不感知 api,option 数据由上层 render/editor 以 props 注入
-->
<template>
  <div v-if="pageType === 'PC'" v-show="visible" class="pc-picker-container">
    <div class="pc-backdrop" @click.stop="onClose"></div>
    <div class="pc-dropdown-panel" @click.stop>
      <div v-if="searchPlaceholder" class="search-box isPC">
        <div class="search-input-wrapper">
          <svg class="search-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="search-input"
          />
        </div>
      </div>
      <div class="option-list isPC" role="listbox">
        <template v-if="groupedOptions.length > 0">
          <template v-for="(group, idx) in groupedOptions">
            <div
              v-if="group.name"
              :key="`group-${group.name}-${idx}`"
              class="option-group-label isPC"
            >
              {{ group.name }}
            </div>
            <button
              v-for="opt in group.options"
              :key="opt.label"
              type="button"
              class="option-row isPC"
              :class="{ selected: opt.value === value }"
              role="option"
              :aria-selected="opt.value === value"
              @click="onSelect(opt.value)"
            >
              <slot name="option" :option="opt">
                <span class="option-label">{{ opt.label }}</span>
              </slot>
            </button>
          </template>
        </template>
        <div v-else class="empty-state isPC">
          {{ t('amoe.picker.empty') || 'No results found' }}
        </div>
      </div>
    </div>
  </div>
  <PanelModal
    v-else
    :visible="visible"
    :page-type="pageType"
    :title="title"
    :show-indicator="true"
    :show-close-button="false"
    :content-padded="false"
    @close="onClose"
  >
    <template #header>
      <h2 v-if="title" class="panel-modal-title">
        {{ title }}
      </h2>
      <div v-if="searchPlaceholder" class="search-box">
        <div class="search-input-wrapper">
          <svg class="search-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="search-input"
          />
        </div>
      </div>
    </template>
    <div class="option-list" role="listbox">
      <template v-if="groupedOptions.length > 0">
        <template v-for="(group, idx) in groupedOptions">
          <div
            v-if="group.name"
            :key="`group-${group.name}-${idx}`"
            class="option-group-label"
          >
            {{ group.name }}
          </div>
          <button
            v-for="opt in group.options"
            :key="opt.label"
            type="button"
            class="option-row"
            :class="{ selected: opt.value === value }"
            role="option"
            :aria-selected="opt.value === value"
            @click="onSelect(opt.value)"
          >
            <slot name="option" :option="opt">
              <span class="option-label">{{ opt.label }}</span>
            </slot>
            <span v-if="opt.value === value" class="check" aria-hidden="true"
              >✓</span
            >
          </button>
        </template>
      </template>
      <div v-else class="empty-state">
        {{ t('amoe.picker.empty') || 'No results found' }}
      </div>
    </div>
  </PanelModal>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, onBeforeUnmount } from 'vue-demi';
import type { PropType } from 'vue-demi';
import { PanelModal } from '@ark/mets-frontend-shared';
import t from '../render/utils/i18n';

import type { PickerOption } from './types';

export default defineComponent({
  name: 'OptionPicker',
  components: { PanelModal },
  props: {
    visible: { type: Boolean, default: false },
    pageType: { type: String as () => 'H5' | 'PC', required: true },
    title: { type: String, required: true },
    searchPlaceholder: { type: String, default: '' },
    options: {
      type: Array as PropType<readonly PickerOption[]>,
      required: true,
    },
    value: { type: String, default: '' },
  },
  emits: ['update:value', 'close'],
  setup(props, { emit }) {
    const searchQuery = ref('');

    watch(
      () => props.visible,
      (newVal) => {
        if (newVal) {
          searchQuery.value = '';
          // 当面板打开时，在 document 上监听点击事件以实现外部点击关闭
          if (props.pageType === 'PC') {
            setTimeout(() => {
              document.addEventListener('click', handleOutsideClick);
            }, 0);
          }
        } else {
          if (props.pageType === 'PC') {
            document.removeEventListener('click', handleOutsideClick);
          }
        }
      }
    );

    // 卸载组件时清理事件监听
    onBeforeUnmount(() => {
      if (props.pageType === 'PC') {
        document.removeEventListener('click', handleOutsideClick);
      }
    });

    function handleOutsideClick() {
      if (props.visible) {
        onClose();
      }
    }

    const filteredOptions = computed(() => {
      const q = searchQuery.value.trim().toLowerCase();
      if (!q) return props.options;
      return props.options.filter((opt) => {
        const label = opt.label.toLowerCase();
        const name = (opt.name || '').toLowerCase();
        const code = (opt.code || '').toLowerCase();
        return label.includes(q) || name.includes(q) || code.includes(q);
      });
    });

    const groupedOptions = computed(() => {
      const isSearching = searchQuery.value.trim().length > 0;
      const result: { name: string; options: PickerOption[] }[] = [];
      let currentGroup: string | null = null;
      let currentOptions: PickerOption[] = [];

      for (const opt of filteredOptions.value) {
        // 搜索时不显示分组字母
        const groupName = isSearching ? '' : (opt.group || '');
        if (groupName !== currentGroup) {
          if (currentOptions.length > 0) {
            result.push({ name: currentGroup || '', options: currentOptions });
          }
          currentGroup = groupName;
          currentOptions = [opt];
        } else {
          currentOptions.push(opt);
        }
      }
      if (currentOptions.length > 0) {
        result.push({ name: currentGroup || '', options: currentOptions });
      }
      return result;
    });
    function onSelect(v: string): void {
      emit('update:value', v);
      emit('close');
    }
    function onClose(): void {
      searchQuery.value = '';
      emit('close');
    }
    return { searchQuery, groupedOptions, onSelect, onClose, t };
  },
});
</script>

<style scoped>
.option-list {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  font-family: MiSans, Arial, sans-serif;
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #0f1112;
  text-align: left;
  box-sizing: border-box;
}

.option-row.selected {
  color: #f36f12;
}

.option-row + .option-row {
  border-top: none;
}

.option-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.check {
  color: #f36f12;
  font-weight: 600;
  flex-shrink: 0;
}

/* H5:选项行 / Figma 触控目标 (默认 mobile-first) */
::v-deep .panel-modal-panel.page-H5 {
  height: 85vh;
  display: flex;
  flex-direction: column;
}

::v-deep .panel-modal-panel.page-H5 .panel-modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.panel-modal-title {
  font-size: 0.42rem;
  line-height: 1.36;
  font-weight: 600;
  color: #0f1112;
  margin: 0.04rem 0.35rem 0.2rem;
  font-family: 'BR Hendrix', Arial, sans-serif;
}

.search-box {
  box-sizing: border-box;
  padding: 0 0.35rem 0.2rem;
}

.search-box .search-input-wrapper {
  display: flex;
  align-items: center;
  background: #F5F6F8;
  border-radius: 0.16rem;
  height: 0.72rem;
  padding: 0 0.24rem;
}

.search-box .search-icon {
  width: 0.32rem;
  height: 0.32rem;
  color: #909499;
  flex-shrink: 0;
  margin-right: 0.16rem;
}

.search-box .search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.28rem;
  color: #0f1112;
  font-family: MiSans, Arial, sans-serif;
}

.search-box .search-input::placeholder {
  color: #BBBDC0;
}

/* PC: 大写 PX 用于跳过 postcss-pxtorem，避免选择器固定尺寸被转成 rem 后受根字号影响 */
.pc-picker-container {
  position: absolute;
  top: calc(100% + 4PX);
  left: 0;
  width: 100%;
  z-index: 1000;
}

.pc-backdrop {
  display: none; /* remove fixed backdrop, rely on document click */
}

.pc-dropdown-panel {
  position: relative;
  background: #ffffff;
  border-radius: 8PX;
  box-shadow: 0 4PX 16PX rgba(0, 0, 0, 0.08);
  border: 1PX solid #E3E4E5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 340PX;
}

.search-box.isPC {
  padding: 12PX 12PX 8PX;
  background: transparent;
  flex-shrink: 0;
}

.search-box.isPC .search-input-wrapper {
  display: flex;
  align-items: center;
  background: #F5F6F8;
  border-radius: 6PX;
  height: 36PX;
  padding: 0 12PX;
}

.search-box.isPC .search-icon {
  width: 16PX;
  height: 16PX;
  color: #909499;
  flex-shrink: 0;
  margin-right: 8PX;
}

.search-box.isPC .search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14PX;
  color: #0f1112;
  font-family: MiSans, Arial, sans-serif;
}

.search-box.isPC .search-input::placeholder {
  color: #BBBDC0;
}

.option-group-label {
  display: flex;
  align-items: flex-end;
  min-height: 0.85rem;
  padding: 0 0.35rem 0.15rem;
  font-size: 0.28rem;
  color: #909499;
  font-weight: 400;
  line-height: 1.4;
  box-sizing: border-box;
}

.option-list {
  /* 高度由 PanelModal 容器控制 */
}

.option-list .option-row {
  min-height: 0.9rem;
  padding: 0 0.35rem;
  font-size: 0.32rem;
  line-height: 1.4;
}

.option-list .option-row.selected {
  background: transparent;
  font-weight: 500;
}

.option-list .option-row + .option-row {
  border-top: none;
}

.option-list.isPC {
  max-height: 260PX;
  overflow-y: auto;
}

.option-list.isPC .option-group-label.isPC {
  min-height: auto;
  padding: 8PX 16PX 4PX;
  font-size: 14PX;
  color: #909499;
  font-weight: 400;
  line-height: 1.4;
  flex-shrink: 0;
}

.option-list.isPC .option-row.isPC {
  min-height: 0;
  height: 40PX;
  padding: 0 16PX;
  font-size: 16PX;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #0f1112;
  font-weight: 400;
  flex-shrink: 0;
}

.option-list.isPC .option-row.isPC:hover {
  background: #F5F6F8;
}

.option-list.isPC .option-row.isPC.selected {
  background: #F5F6F8;
  color: #0f1112; /* screenshot shows selected option in black, not orange */
  font-weight: 400;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  color: #909499;
  font-size: 0.28rem;
  min-height: 2rem;
}

.empty-state.isPC {
  font-size: 14PX;
  min-height: 100PX;
}
</style>
